import React, { useMemo } from 'react'
import { useSelector } from 'react-redux';
import { PortInfo, setObjectType } from '../../utils';
import { validateConnection } from '../../utils/DesignerUtils';
import { Connection, Handle, NodeProps, Position } from 'reactflow';
import { EventEmitter } from '../../utils/EventEmitter';
import { getEnvironmentVariableSelector, getUsedItemSelector } from '../../slices/designerSlice';
import { UsedItem } from '../../models/generics';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaService } from '../../models/BdaService';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Col, Row } from 'react-bootstrap';
import { Avatar, Flex, Tooltip } from 'antd';
import TextResizer from '../generics/TextResizer';

type AgentNodeData = Record<string, string> & {
  inputs: PortInfo[];
  outputs: PortInfo[];
  text: string;
  label: string;
}

export default function AgentNode({ id, data, isConnectable }: NodeProps<AgentNodeData>) {
  const getUsedItem = useSelector(getUsedItemSelector);
  const getSelectedEnvironmentVariable = useSelector(getEnvironmentVariableSelector);
  const llmModelKey = window.__env.DESIGNER__AGENT_LLM_MODEL_PROPERTY, promptKey = window.__env.DESIGNER__AGENT_PROMPT_PROPERTY;
  const selectedEnvironmentVariable = useMemo(() => getSelectedEnvironmentVariable(data[llmModelKey]), [data, getSelectedEnvironmentVariable, llmModelKey]);

  const isValidConnection = (connection: Connection): boolean => {
    const isValid = connection.source !== connection.target && validateConnection(connection.sourceHandle!, connection.targetHandle!);
    if (!isValid) return false;

    const sourceItem = getUsedItem(connection.source)!;

    if (sourceItem.type === 'dataset' && setObjectType<UsedItem<BdaDataset>>(sourceItem)) { // targetItem can only be the current service
      const input = data.inputs.find(input => input.id === connection.targetHandle)!;
      return (sourceItem.datasource!.type === 'messageBroker' && input.datasetMode === 'streaming') || (sourceItem.datasource!.type !== 'messageBroker' && input.datasetMode !== 'streaming');
    }
    else if ((sourceItem.type === 'service') && setObjectType<UsedItem<BdaService>>(sourceItem)) { // TODO replace service type with just 'service' ???
      const targetItem = getUsedItem(connection.target) as UsedItem<BdaService>;
      const sourceOutput = (sourceItem.outputs as PortInfo[]).find(output => output.id === connection.sourceHandle);
      const targetInput = (targetItem.inputs as PortInfo[]).find(input => input.id === connection.targetHandle);
      return sourceOutput?.datasetMode === targetInput?.datasetMode;
    }

    return true;
  }
  const handlePortClick = (portId: string, generic?: boolean, description?: string) => (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.stopPropagation();

    const portInfo: PortInfo = {
      id: portId,
      nodeId: id,
      nodeName: data.text,
      description,
      generic,
    };
    EventEmitter.dispatch('selected-port', portInfo);
  }

  return (
    <div className="designer-node agent-node">
      <div className="input-ports">
        {data.inputs.map(({ id, type, datasetMode, generic, description }) => (
          <Handle
            className={classNames("custom-handle service-handle", type, datasetMode, { generic })}
            key={id}
            type="target"
            position={Position.Top}
            id={id}
            isConnectable={isConnectable}
            isValidConnection={isValidConnection}
            onClick={handlePortClick(id, generic, description)}
          />
        ))}
      </div>

      <h6 className="mb-0">
        <FontAwesomeIcon icon={solid('user-secret')} />
        <span className="ms-2">{data.text || data.label}</span>
      </h6>
      {(data[llmModelKey] !== undefined || data[promptKey] !== undefined) && (
        <Row className="inputs-row gx-2">
          <Col xs="auto">
            <div className={classNames("input-container", { empty: !selectedEnvironmentVariable })}>
              {selectedEnvironmentVariable
                ? (
                  <Flex align="center">
                    <div>
                      <Avatar
                        src={selectedEnvironmentVariable.img}
                        icon={<FontAwesomeIcon icon={solid('image')} />}
                        className="border me-1"
                        style={{ height: 35, width: 35 }}
                      />
                    </div>
                    <Col className="ps-2 overflow-hidden text-truncate">
                      <Tooltip title={selectedEnvironmentVariable.name}>
                        <b className="d-block mb-0 text-truncate">{selectedEnvironmentVariable.name}</b>
                      </Tooltip>
                      <small className="text-muted">{selectedEnvironmentVariable.description}</small>
                    </Col>
                  </Flex>
                )
                : (
                  <>
                    <FontAwesomeIcon icon={solid('plus')} />
                    <span className="ms-2">Choose LLM Model</span>
                  </>
                )}
            </div>
          </Col>
          <Col>
            <div className={classNames("input-container", { empty: !data[promptKey] })}>
              {data[promptKey]
                ? (
                  <Flex align="center">
                    <FontAwesomeIcon icon={solid('terminal')} size="sm" />
                    <TextResizer className="ms-2">{data[promptKey]}</TextResizer>
                  </Flex>
                )
                : (
                  <>
                    <FontAwesomeIcon icon={solid('plus')} />
                    <span className="ms-2">Write Prompt</span>
                  </>
                )}
            </div>
          </Col>
        </Row>
      )}

      <div className="output-ports">
        {data.outputs.map(({ id, type, datasetMode, generic, description }) => (
          <Handle
            type="source"
            key={id}
            className={classNames("custom-handle service-handle", type, datasetMode, { generic })}
            position={Position.Bottom}
            id={id}
            isConnectable={isConnectable}
            isValidConnection={isValidConnection}
            onClick={handlePortClick(id, generic, description)}
          />
        ))}
      </div>
    </div>
  );
}
